import React, { useEffect, useRef, useState } from 'react';
import Webcam from 'react-webcam';
import { ReactMediaRecorder } from 'react-media-recorder';
import { Button, Spinner } from 'flowbite-react';
import { Loader } from 'ui';
import VideoResumeDisclaimerModal from './VideoResumeDisclaimerModal';
import { useNavigate } from 'react-router-dom';
import UAParser from 'ua-parser-js';
import { get, map } from 'lodash';
import fixWebmDuration from 'webm-duration-fix';
import { useUserContext } from 'src/context/UserProvider';
import { useToast } from 'src/context/ToastProvider';
import useDisclosure from 'src/hooks/utils/useDisclosure';
import { UserRole } from 'types';
import { getPolicyForS3 } from 'src/apis/user';
import { useGetVideoResumeTemplateByRoleQuery } from 'src/hooks/videoResume.hooks';
import { useMutation, useQueryClient } from 'react-query';
import { createVideoResume } from 'src/apis/videoResume';

const VideoAnswerScreen = () => {
	const webcamRef = useRef<Webcam>(null);
	const { isOpen, onClose, onOpen } = useDisclosure();
	const toast = useToast();
	const { user: userData } = useUserContext();
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	/** assign mime type */
	const uaParser = new UAParser();
	const osName = uaParser.getOS().name;
	const browserName = uaParser.getBrowser().name;
	const mimeType =
		osName?.toLowerCase().includes('ios') || browserName?.toLowerCase().includes('safari') ? 'video/mp4' : 'video/webm';

	const [startTime, setStartTime] = useState<number>();
	const [elapsedTime, setElapsedTime] = useState(0);
	const [recordedVideo, setRecordedVideo] = useState<Blob | null>(null);
	const [videoAnswerBlobArray, setVideoAnswerBlobArray] = useState<Blob[]>([]);
	const [videoAnswerURlArray, setVideoAnswerURlArray] = useState<
		{
			question: string;
			url: string;
		}[]
	>([]);
	const [isRecordingStarted, setIsRecordingStarted] = useState<boolean>(false);
	const [currentQuestion, setCurrentQuestion] = useState<{
		index: number;
		question?: string;
	}>();
	const [questions, setQuestions] = useState<string[]>([]);
	const [completedResume, setCompletedResume] = useState<boolean>(false);

	const [uploadProgress, setUploadProgress] = useState<number>(0);
	const [isUploadingVideo, setiIsUploadingVideo] = useState<boolean>(false);

	const { data, isLoading } = useGetVideoResumeTemplateByRoleQuery(userData?.role as UserRole);

	const createVideoResumeFn = useMutation(['createVideoResume'], createVideoResume, {
		onSuccess: () => {
			queryClient.refetchQueries(['verifyCookie']);
			setCompletedResume(false);
			setVideoAnswerBlobArray([]);
			setVideoAnswerURlArray([]);
			onClose();
			toast({
				title: 'Video Resume submitted Successfully',
				type: 'success',
			});
			// navigate('/dashboard');
			navigate('/my-applications');
		},
		onError: (error: Error) => {
			console.log(error);
			setCompletedResume(false);
			onClose();
			const errorMessage = get(error, ['response', 'data', 'message'], error.message);
			toast({
				type: 'error',
				title: errorMessage,
			});
		},
	});

	const handleRecordingStart = () => {
		setStartTime(Date.now()); // Set the start time when recording starts
	};

	const handleVideoStop = (blobUrl: string, blob: Blob) => {
		if (mimeType === 'video/webm') {
			fixWebmDuration(
				new Blob([blob], {
					type: mimeType,
				}),
			)
				.then(fixedBlob => {
					// video webm duration set!
					const temp = fixedBlob;
					setRecordedVideo(temp);
					setStartTime(undefined);
				})
				.catch(err => {
					// Fixing video duration of webm failed, using unfixed blob
					console.error(err);
					const temp = new Blob([blob], { type: mimeType });
					setRecordedVideo(temp);
					setStartTime(undefined);
				});
		} else {
			const temp = new Blob([blob], { type: mimeType });
			setRecordedVideo(temp);
			setStartTime(undefined);
		}
	};

	const handleNextQuestion = async () => {
		if (recordedVideo && currentQuestion) {
			setiIsUploadingVideo(true);
			setVideoAnswerBlobArray([...videoAnswerBlobArray, recordedVideo]);
			const policy = await getPolicyForS3(recordedVideo.type, userData?._id || '', 'videoResume');
			handleS3Upload(policy, recordedVideo);
		}
	};

	const handleSubmit = async () => {
		if (recordedVideo && currentQuestion) {
			onClose();
			setiIsUploadingVideo(true);
			setVideoAnswerBlobArray([...videoAnswerBlobArray, recordedVideo]);
			const policy = await getPolicyForS3(recordedVideo.type, userData?._id || '', 'videoResume');
			handleS3Upload(policy, recordedVideo);
			setCompletedResume(true);
		}
	};

	useEffect(() => {
		if (data?.template) {
			const parsedQuestions = JSON.parse(data.template.questions);
			if (parsedQuestions.length) {
				setCurrentQuestion({
					index: 0,
					question: parsedQuestions[0],
				});
				setQuestions(parsedQuestions);
			}
		}
	}, [data]);

	useEffect(() => {
		if (
			questions.length &&
			userData?.profile &&
			data?.template &&
			videoAnswerURlArray.length === questions.length &&
			!createVideoResumeFn.isLoading
		) {
			const payload = {
				title: `Video Resume - ${userData?.name}`,
				urls: { questionMapping: videoAnswerURlArray },
				videoResumeTemplate: data.template._id,
			};
			createVideoResumeFn.mutate(payload);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [videoAnswerURlArray]);

	useEffect(() => {
		let timerId: any;

		if (startTime) {
			timerId = setInterval(() => {
				setElapsedTime(Math.round((Date.now() - startTime) / 1000)); // Update elapsed time every second
			}, 1000);
		}

		return () => {
			clearInterval(timerId); // Clean up the timer on component unmount
		};
	}, [startTime]);

	useEffect(() => {
		window.onbeforeunload = function (e) {
			return 'Are you sure you want to leave?';
		};

		// router.events.on('routeChangeStart', handleBrowseAway);
		return () => {
			window.onbeforeunload = null;
			// router.events.off('routeChangeStart', handleBrowseAway);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleS3Upload = (
		policyResponse: {
			success?: boolean;
			policy?: any;
			data?: any;
			filePath?: string;
			errorMessage?: string;
		},
		blob: Blob,
	) => {
		if (!policyResponse.success) {
			// show error
			throw new Error('Failed to get policy');
		}

		const url = get(policyResponse.policy, 'url');
		const endpoint = `${url}/${policyResponse.filePath}`;

		const formData = new FormData();
		map(policyResponse.policy.fields, (value, key) => {
			formData.append(key, value);
		});

		formData.append('file', blob);

		// xhr post
		var xhr = new XMLHttpRequest();
		xhr.open('POST', url, true);
		xhr.upload.onprogress = e => {
			if (e.lengthComputable) {
				const percentage: number = Math.floor(Math.floor((10 * 100 * e.loaded) / e.total) / 10);
				setUploadProgress(percentage);
			}
		};
		xhr.upload.onloadstart = () => {
			// promt -- uploading
		};
		xhr.upload.onload = () => {
			if (currentQuestion) {
				const question = questions[currentQuestion.index];
				setVideoAnswerURlArray([...videoAnswerURlArray, { url: endpoint, question }]);
			}

			if (currentQuestion && currentQuestion.index < questions.length - 1) {
				setRecordedVideo(null);
				setStartTime(undefined);
				setElapsedTime(0);
				setCurrentQuestion({
					index: currentQuestion.index + 1,
					question: questions[currentQuestion.index + 1],
				});
			} else {
				setCompletedResume(true);
			}

			setUploadProgress(0);
			setiIsUploadingVideo(false);
		};
		xhr.onerror = e => {
			// promt -- error
			toast({
				type: 'error',
				title: 'Video upload failed, please try again',
			});
			setUploadProgress(0);
			setiIsUploadingVideo(false);
			if (completedResume) setCompletedResume(false);
			onClose();
		};
		xhr.send(formData);
	};

	if (isLoading || !questions.length || !currentQuestion) return <Loader />;
	const minutes = Math.floor(elapsedTime / 60);
	const seconds = elapsedTime - minutes * 60;

	return (
		<div>
			<div className="flex justify-center">
				{recordedVideo ? (
					<div className="flex h-auto w-[82%] flex-col items-center justify-start rounded-[16px] bg-[#1F2A37] py-[30px] sm:mt-[24px]">
						<div className="relative flex max-w-[700px] flex-col items-center justify-start sm:w-full">
							<div className="mb-4 w-full px-5">
								<div className="text-[12px] font-medium text-[#C7C9D9]">
									Question {currentQuestion.index + 1} of {questions.length}
								</div>
								<div className="text-[20px] font-bold text-white">{currentQuestion.question}</div>
							</div>
							<video controls>
								<source src={URL.createObjectURL(recordedVideo)} type={recordedVideo.type} />
							</video>

							<div className="mt-[36px] flex w-full justify-center gap-4">
								<Button
									style={{
										border: '1px solid #7777E7',
									}}
									color={'dark'}
									disabled={isUploadingVideo}
									onClick={() => setRecordedVideo(null)}
								>
									Retake
								</Button>
								{currentQuestion.index === questions.length - 1 ? (
									<Button color="purplePlaced" disabled={isUploadingVideo} onClick={onOpen}>
										{isUploadingVideo ? <Spinner /> : 'Submit'}
									</Button>
								) : (
									<Button color="purplePlaced" disabled={isUploadingVideo} onClick={handleNextQuestion}>
										{isUploadingVideo ? <Spinner /> : 'Next Question'}
									</Button>
								)}
							</div>

							{isUploadingVideo ? (
								<div className="absolute left-0 top-0 flex h-full w-full flex-col items-center justify-center">
									<div
										style={{
											border: '0.5px solid grey',
										}}
										className="text-md w-[400px] rounded-[12px] bg-[#ffffff85] p-4 text-center font-medium sm:w-full"
									>
										<Spinner /> Uploading...{uploadProgress}%
									</div>
								</div>
							) : null}
						</div>
					</div>
				) : (
					<div className=" relative rounded-[16px] bg-[#1F2A37] p-5 sm:mt-[24px]">
						<div className="sm:!opacity-1 absolute left-5 top-5 z-[40] flex w-[900px] justify-between bg-black px-4 py-2 sm:static sm:w-full sm:bg-[#1F2A37] md:!opacity-60">
							<div>
								<div className="text-[12px] font-medium text-[#C7C9D9]">
									Question {currentQuestion.index + 1} of {questions.length}
								</div>
								<div className="mt-2 text-[20px] font-bold text-white">{currentQuestion.question}</div>
							</div>
							{isRecordingStarted ? (
								<div className="absolute right-[16px] top-[6px] z-20 flex w-max items-center gap-2 sm:right-[38px] sm:top-[28px] sm:text-xs">
									<div className="text-white">
										{minutes ? `${minutes}m` : ''} {seconds}s
									</div>
									<div className="h-3 w-3 rounded-full bg-red-700" />
									<span className="text-white">Recording</span>
								</div>
							) : null}
						</div>
						<ReactMediaRecorder
							video
							onStop={handleVideoStop}
							onStart={handleRecordingStart}
							render={({ startRecording, stopRecording }) => (
								<div className="relative w-[900px] sm:w-full">
									<Webcam
										style={{
											width: '100%',
										}}
										videoConstraints={{ facingMode: 'user' }}
										audioConstraints={{ echoCancellation: true, noiseSuppression: true }}
										mirrored={true}
										ref={webcamRef}
									/>
									<div className="absolute bottom-0 flex w-full justify-end bg-[#00000071] px-[24px] py-[6px]">
										{isRecordingStarted ? (
											<Button
												color="purplePlaced"
												onClick={() => {
													stopRecording();
													setIsRecordingStarted(false);
												}}
											>
												End Recording
											</Button>
										) : (
											<Button
												color="purplePlaced"
												onClick={() => {
													startRecording();
													setIsRecordingStarted(true);
												}}
											>
												Start Recording
											</Button>
										)}
									</div>
								</div>
							)}
						/>
					</div>
				)}
			</div>
			<VideoResumeDisclaimerModal
				isOpen={isOpen}
				onClose={onClose}
				onSubmit={handleSubmit}
				isLoading={createVideoResumeFn.isLoading || completedResume}
			/>
		</div>
	);
};

export default VideoAnswerScreen;
